import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { canAdminDocuments } from "@/lib/document/documentRoles";
import { deleteDocCategory, upsertDocCategory } from "@/lib/document/docClient";
import type { DocCategoryNode } from "@/lib/document/categoryTypes";
import {
  findDocCategoryNode,
  nextDocCategorySortOrder,
} from "@/lib/document/documentCategoryOptions";
import { DocumentCategoryTree } from "./DocumentCategoryTree";

type Props = {
  tree: DocCategoryNode[];
  flatCategories: Omit<DocCategoryNode, "children">[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onChanged: () => void;
  loading?: boolean;
};

type EditorMode =
  | { kind: "create"; parentId: string | null; label: string }
  | { kind: "rename"; categoryId: string; currentName: string };

export function DocumentCategoryManager({
  tree,
  flatCategories,
  selectedId,
  onSelect,
  onChanged,
  loading = false,
}: Props) {
  const auth = useAuth();
  const canAdmin = canAdminDocuments(auth);
  const [editorMode, setEditorMode] = useState<EditorMode | null>(null);
  const [nameInput, setNameInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedNode = useMemo(
    () => (selectedId ? findDocCategoryNode(tree, selectedId) : null),
    [tree, selectedId],
  );

  function openCreate(parentId: string | null, label: string) {
    setEditorMode({ kind: "create", parentId, label });
    setNameInput("");
    setError(null);
  }

  function openRename(categoryId: string, currentName: string) {
    setEditorMode({ kind: "rename", categoryId, currentName });
    setNameInput(currentName);
    setError(null);
  }

  function cancelEditor() {
    setEditorMode(null);
    setNameInput("");
    setError(null);
  }

  async function submitEditor() {
    const name = nameInput.trim();
    if (!name || !editorMode) return;

    setBusy(true);
    setError(null);

    if (editorMode.kind === "create") {
      const r = await upsertDocCategory({
        name,
        parent_id: editorMode.parentId,
        sort_order: nextDocCategorySortOrder(flatCategories, editorMode.parentId),
      });
      setBusy(false);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      onSelect(r.data.category.id);
      cancelEditor();
      onChanged();
      return;
    }

    const r = await upsertDocCategory({
      id: editorMode.categoryId,
      name,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    cancelEditor();
    onChanged();
  }

  async function handleDelete() {
    if (!selectedId || !selectedNode || selectedNode.is_system_default) return;
    if (!window.confirm(`Delete folder "${selectedNode.name}"? This cannot be undone.`)) return;

    setBusy(true);
    setError(null);
    const r = await deleteDocCategory(selectedId);
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    onChanged();
  }

  const canDeleteSelected =
    canAdmin && selectedNode && !selectedNode.is_system_default && selectedNode.children.length === 0;

  return (
    <div className="space-y-3">
      {loading ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Loading categories…</p>
      ) : (
        <DocumentCategoryTree nodes={tree} selectedId={selectedId} onSelect={onSelect} />
      )}

      {canAdmin ? (
        <div className="space-y-2 border-t border-gray-100 pt-3 dark:border-zinc-800">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => openCreate(null, "New category")}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-600 dark:hover:bg-zinc-800"
            >
              + Category
            </button>
            {selectedId ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => openCreate(selectedId, "New subcategory")}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-600 dark:hover:bg-zinc-800"
              >
                + Subcategory
              </button>
            ) : null}
            {selectedNode ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => openRename(selectedNode.id, selectedNode.name)}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-600 dark:hover:bg-zinc-800"
              >
                Rename
              </button>
            ) : null}
            {canDeleteSelected ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleDelete()}
                className="rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/30"
              >
                Delete
              </button>
            ) : null}
          </div>

          {editorMode ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 dark:border-zinc-700 dark:bg-zinc-900/40">
              <p className="mb-2 text-xs font-medium text-gray-600 dark:text-zinc-400">
                {editorMode.kind === "create" ? editorMode.label : "Rename folder"}
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <label className="min-w-[10rem] flex-1 text-xs">
                  <span className="text-gray-500">Name</span>
                  <input
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="Folder name"
                    className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-800"
                  />
                </label>
                <button
                  type="button"
                  disabled={busy || !nameInput.trim()}
                  onClick={() => void submitEditor()}
                  className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
                >
                  {busy ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={cancelEditor}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs dark:border-zinc-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}

          {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}

          <p className="text-[11px] text-gray-500 dark:text-zinc-500">
            Default folders can be renamed. Custom folders can be nested one or more levels deep.
          </p>
        </div>
      ) : null}
    </div>
  );
}
